import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { 
  TrendingDown,
  TrendingUp,
  Users, 
  DollarSign, 
  Smartphone,
  Wifi,
  CheckCircle2,
  AlertTriangle,
  Target,
  Building2,
  Signal,
  Calendar,
  CalendarRange,
  FileText,
  MapPin,
  Check
} from 'lucide-react';
import { ClaroEmpresasHeader } from '@/components/dashboard/ClaroEmpresasHeader';
import { ClaroHeader } from './ClaroHeader';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useMemo } from 'react';

interface ItemDetalhado {
  descricao: string;
  valor: number;
  nivel: number;
  tipo: 'mensalidade' | 'pacote' | 'servico';
}

interface FaturaAnaliseDetalhadaProps {
  analise: any;
  ocultarHeader?: boolean;
}

/**
 * Formata valor monetário de forma segura, tolerando null/undefined/strings.
 * Evita "Cannot read properties of null (reading 'toFixed')" quando a análise
 * vem parcial do OCR/n8n.
 */
const fmtBRL = (v: any, dec = 2): string => {
  const n = typeof v === 'number' && isFinite(v)
    ? v
    : (typeof v === 'string' && isFinite(Number(v)) ? Number(v) : 0);
  return n.toFixed(dec);
};

const safeNum = (v: any, fallback = 0): number => {
  if (typeof v === 'number' && isFinite(v)) return v;
  if (typeof v === 'string' && isFinite(Number(v))) return Number(v);
  return fallback;
};

/**
 * Extrai a data de fidelidade do texto OCR para um MSISDN específico
 */
function extrairFidelidadePorMSISDN(ocrTexto: string, msisdn: string): string | null {
  if (!ocrTexto || !msisdn) return null;
  
  const msisdnLimpo = msisdn.replace(/\D/g, '');
  
  const pattern = new RegExp(
    `\\(?${msisdnLimpo.slice(0, 2)}\\)?\\s*` +
    `${msisdnLimpo.slice(2, 7)}\\s*` +
    `${msisdnLimpo.slice(7)}\\s*` +
    `Fidelidade\\s+at\\s+[éeE]\\s+` +
    `(\\d{2})/(\\d{2})/(\\d{4})`,
    'i'
  );
  
  const match = ocrTexto.match(pattern);
  
  if (match) {
    const dataFormatada = `${match[3]}-${match[2]}-${match[1]}`;
    console.log('✅ Fidelidade encontrada:', { msisdn, data: dataFormatada });
    return dataFormatada;
  }
  
  console.log('❌ Fidelidade NÃO encontrada para:', msisdn);
  return null;
}

export function FaturaAnaliseDetalhada({ analise, ocultarHeader = false }: FaturaAnaliseDetalhadaProps) {
  const getCurrentData = () => {
    return analise?.json_resultado;
  };

  const { data: ocrData } = useQuery({
    queryKey: ['fatura-ocr', analise?.fatura_id],
    queryFn: async () => {
      if (!analise?.fatura_id) return null;
      
      const { data, error } = await supabase
        .from('fatura_ocr')
        .select('ocr_texto')
        .eq('fatura_id', analise.fatura_id)
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (error) {
        console.error('❌ Erro ao buscar OCR:', error);
        return null;
      }
      
      return data?.[0]?.ocr_texto || null;
    },
    enabled: !!analise?.fatura_id,
    staleTime: 5 * 60 * 1000,
  });

  const analiseEnriquecida = useMemo(() => {
    const baseData = getCurrentData();
    if (!baseData?.analise_detalhada?.linhas || !ocrData) return baseData;
    
    const linhasEnriquecidas = baseData.analise_detalhada.linhas.map((linha: any) => {
      const dataFidelidade = extrairFidelidadePorMSISDN(ocrData, linha.msisdn);
      
      return {
        ...linha,
        situacao_atual: {
          ...linha.situacao_atual,
          data_fidelidade: dataFidelidade || linha.situacao_atual.data_fidelidade
        }
      };
    });
    
    return {
      ...baseData,
      analise_detalhada: {
        ...baseData.analise_detalhada,
        linhas: linhasEnriquecidas
      }
    };
  }, [analise, ocrData]);

  if (!analiseEnriquecida) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-warning" />
        <p className="text-lg font-semibold mb-2">Nenhuma análise disponível</p>
        <p className="text-sm text-muted-foreground">
          A fatura ainda não foi processada.
        </p>
      </div>
    );
  }

  if (!analiseEnriquecida.resumo_executivo || !analiseEnriquecida.analise_detalhada || !analiseEnriquecida.proposta_final) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-warning" />
        <p className="text-lg font-semibold mb-2">Estrutura de análise incompleta</p>
        <p className="text-sm text-muted-foreground mb-4">
          A fatura foi processada mas a análise detalhada não está disponível.
        </p>
        <div className="text-xs text-muted-foreground text-left max-w-md mx-auto">
          <p className="font-medium mb-2">Estrutura recebida:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>resumo_executivo: {analiseEnriquecida.resumo_executivo ? '✓' : '✗'}</li>
            <li>analise_detalhada: {analiseEnriquecida.analise_detalhada ? '✓' : '✗'}</li>
            <li>proposta_final: {analiseEnriquecida.proposta_final ? '✓' : '✗'}</li>
          </ul>
        </div>
      </div>
    );
  }

  const { resumo_executivo, analise_detalhada, proposta_final } = analiseEnriquecida;

  return (
    <div className="space-y-4">
      {!ocultarHeader && (
        <>
          {/* HEADER CLARO EMPRESAS */}
          <ClaroEmpresasHeader 
            titulo={resumo_executivo.cliente.razao_social}
            subtitulo={`CNPJ: ${resumo_executivo.cliente.cnpj} • Conta: ${resumo_executivo.cliente.conta_numero || 'N/D'}`}
            className="rounded-lg shadow-lg"
          />

          {/* 1. RESUMO EXECUTIVO */}
          <Card className="border shadow-md bg-card overflow-hidden">
            <CardHeader className="pb-4 pt-5 px-6 bg-card space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-primary" />
                  <span className="text-muted-foreground">Vencimento:</span>
                  <span className="font-semibold text-foreground">
                    {resumo_executivo.periodo.vencimento ? new Date(resumo_executivo.periodo.vencimento).toLocaleDateString('pt-BR') : 'N/D'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <CalendarRange className="h-4 w-4 text-primary" />
                  <span className="text-muted-foreground">Período:</span>
                  <span className="font-semibold text-foreground">
                    {resumo_executivo.periodo.inicio} a {resumo_executivo.periodo.fim}
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-2 pt-2 border-t border-border">
                <MapPin className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground text-sm">
                  {resumo_executivo.cliente.endereco}
                </span>
              </div>
            </CardHeader>
          </Card>
        </>
      )}

      {/* 2. COMPARATIVO VISUAL: ANTES X DEPOIS */}
      <Card className="border shadow-md bg-card">
        <ClaroHeader>
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 !text-white" />
            <span>Comparativo: Situação Atual vs. Proposta</span>
          </div>
        </ClaroHeader>
        <CardContent className="pt-6 pb-6 space-y-6 bg-card">
          {/* SITUAÇÃO ATUAL */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span>Situação Atual</span>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Linhas', value: resumo_executivo.situacao_atual.linhas_ativas },
                { label: 'Total GB', value: `${resumo_executivo.situacao_atual.gb_total_contratado}GB` },
                { label: 'Valor Mensal', value: `R$ ${fmtBRL(resumo_executivo.situacao_atual.valor_total_mensal)}` },
                { label: 'Por Linha', value: `R$ ${fmtBRL(resumo_executivo.situacao_atual.valor_por_linha)}` }
              ].map((kpi, i) => (
                <div key={i} className="rounded-lg border border-border bg-card p-4 shadow-sm flex flex-col items-center justify-center">
                  <div className="text-lg md:text-xl font-bold text-foreground">
                    {kpi.value}
                  </div>
                  <div className="mt-1 text-xs font-medium text-muted-foreground uppercase tracking-wide text-center">
                    {kpi.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* PROPOSTA DE OTIMIZAÇÃO */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="text-sm font-semibold text-primary uppercase tracking-wide flex items-center gap-2">
                <Target className="h-4 w-4 text-primary" />
                <span>Proposta de Otimização</span>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Linhas', value: resumo_executivo.situacao_atual.linhas_ativas },
                { label: 'Total GB', value: `${proposta_final.gb_total_contratado}GB` },
                { label: 'Valor Mensal', value: `R$ ${fmtBRL(proposta_final.valor_mensal_total)}` },
                { label: 'Por Linha', value: `R$ ${fmtBRL(proposta_final.valor_por_linha)}` }
              ].map((kpi, i) => (
                <div key={i} className="rounded-lg border-2 border-primary/30 bg-primary/5 p-4 shadow-sm flex flex-col items-center justify-center">
                  <div className="text-lg md:text-xl font-bold text-primary">
                    {kpi.value}
                  </div>
                  <div className="mt-1 text-xs font-medium text-muted-foreground uppercase tracking-wide text-center">
                    {kpi.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 3. PROPOSTA DE OTIMIZAÇÃO (P2) - DETALHADA */}
      <Card className="border shadow-md bg-card">
        <ClaroHeader className="rounded-t-lg rounded-b-none">
          <div className="flex items-center gap-2">
            <TrendingDown className="h-5 w-5 !text-white" />
            <span>Proposta de Otimização – Máxima Economia</span>
          </div>
        </ClaroHeader>

        <CardContent className="px-5 py-6 space-y-4 bg-card">
          <p className="text-muted-foreground text-sm mb-4">
            {proposta_final.descricao}
          </p>

          {/* Grid de valores principais */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="text-center p-4 rounded-lg bg-card border-2 border-primary shadow-sm">
              <div className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                Valor Mensal Total Novo
              </div>
              <div className="flex justify-center">
                <span className="text-2xl md:text-3xl font-bold text-primary">
                  R$ {fmtBRL(proposta_final.valor_mensal_total)}
                </span>
              </div>
            </div>

            <div className="text-center p-4 rounded-lg bg-card border border-border shadow-sm">
              <div className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                Valor Por Linha
              </div>
              <div className="flex justify-center">
                <span className="text-lg md:text-xl font-semibold text-foreground">
                  R$ {fmtBRL(proposta_final.valor_por_linha)}
                </span>
              </div>
            </div>

            <div className="text-center p-4 rounded-lg bg-card border border-border shadow-sm">
              <div className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                Total GB Proposto
              </div>
              <div className="flex justify-center">
                <span className="text-lg md:text-xl font-semibold text-foreground">
                  {proposta_final.gb_total_contratado}GB
                </span>
              </div>
            </div>
          </div>

          {/* Seção de Economia */}
          <div className="pt-4 border-t border-border">
            <div className="flex items-center justify-between mb-3">
              <span className="text-muted-foreground font-medium text-sm flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                <span>Economia Mensal</span>
              </span>
              <span className="text-success font-bold text-xl">
                R$ {fmtBRL(proposta_final.economia_mensal)}
              </span>
            </div>
            
            <div className="flex items-center justify-between mb-3">
              <span className="text-muted-foreground font-medium text-sm flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>Economia Anual</span>
              </span>
              <span className="text-success font-bold text-xl">
                R$ {fmtBRL(proposta_final.economia_anual)}
              </span>
            </div>

            <div className="w-full h-3 rounded-full bg-muted-foreground overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary to-[#8B1A1E] transition-all duration-500"
                style={{ width: proposta_final.economia_percent }}
              />
            </div>
            
            <div className="mt-2 text-right text-primary text-sm font-semibold">
              {proposta_final.economia_percent} de economia
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 4. ANÁLISE DETALHADA POR LINHA */}
      <Card className="!bg-card border shadow-md">
        <ClaroHeader className="rounded-t-lg rounded-b-none flex items-center gap-2">
          <Smartphone className="h-5 w-5" />
          Análise Detalhada por Linha
        </ClaroHeader>
        <CardContent className="!bg-card pt-4 pb-4">
          <Tabs defaultValue="linha-0" className="w-full">
            <TabsList className="grid w-full !bg-card !border !border-border p-1 rounded-lg shadow-sm" style={{ gridTemplateColumns: `repeat(${analise_detalhada.linhas.length}, 1fr)` }}>
              {analise_detalhada.linhas.map((linha: any, idx: number) => (
                <TabsTrigger key={idx} value={`linha-${idx}`} className="text-xs !text-muted-foreground data-[state=active]:!bg-primary data-[state=active]:!text-white data-[state=active]:font-semibold rounded-md transition-all">
                  {linha.msisdn.replace(/\((\d{2})\)\s*(\d+)\s*(\d{4})/, '(...) $3')}
                </TabsTrigger>
              ))}
            </TabsList>
            
            {analise_detalhada.linhas.map((linha: any, idx: number) => (
              <TabsContent key={idx} value={`linha-${idx}`} className="space-y-3 mt-3 !bg-card">
                {/* Situação Atual - Formato tabela/grid limpo */}
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="bg-primary text-white px-4 py-2.5 text-sm font-semibold flex items-center gap-2">
                    <Smartphone className="h-4 w-4" />
                    Situação Atual - {linha.msisdn}
                    {linha.situacao_atual.data_fidelidade && (
                      <span className="text-xs font-normal opacity-80 ml-2">
                        • Fidelidade até {new Date(linha.situacao_atual.data_fidelidade).toLocaleDateString('pt-BR')}
                      </span>
                    )}
                  </div>
                  
                  {/* Grid de dados no formato tabela */}
                  <div className="divide-y divide-border">
                    <div className="grid grid-cols-2 gap-0 divide-x divide-border">
                      <div className="p-3">
                        <p className="text-xs text-muted-foreground font-medium">Plano Atual</p>
                        <p className="font-semibold text-sm text-foreground mt-0.5">{linha.situacao_atual.plano}</p>
                      </div>
                      <div className="p-3">
                        <p className="text-xs text-muted-foreground font-medium">Valor Mensal</p>
                        <p className="font-semibold text-sm text-foreground mt-0.5">R$ {fmtBRL(linha.situacao_atual?.valor)}</p>
                      </div>
                      <div className="p-3">
                        <p className="text-xs text-muted-foreground font-medium">GB Elegível Renovação</p>
                        <p className="font-semibold text-sm text-primary mt-0.5">{linha.situacao_atual.gb_elegivel_renovacao}GB</p>
                        <p className="text-xs text-muted-foreground">
                          ({linha.situacao_atual.gb_nominal}GB nominal + {linha.situacao_atual.gb_bonus_turbo}GB turbo)
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-0 divide-x divide-border">
                      <div className="p-3">
                        <p className="text-xs text-muted-foreground font-medium">Uso Real</p>
                        <p className="font-semibold text-sm text-foreground mt-0.5">{linha.situacao_atual.uso_gb} GB</p>
                      </div>
                      <div className="p-3">
                        <p className="text-xs text-muted-foreground font-medium">Redes Sociais</p>
                        <p className="font-semibold text-sm text-foreground mt-0.5">{linha.situacao_atual.gb_redes_sociais}GB</p>
                        <p className="text-xs text-muted-foreground">(não conta na renovação)</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Mensalidades e Pacotes */}
                  {linha.situacao_atual.itens_detalhados && linha.situacao_atual.itens_detalhados.length > 0 && (
                    <div className="border-t border-border">
                      <div className="bg-primary/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-primary">
                        Mensalidades e Pacotes Promocionais
                      </div>
                      
                      <div className="divide-y divide-border">
                        {linha.situacao_atual.itens_detalhados.map((item: ItemDetalhado, i: number) => (
                          <div 
                            key={i}
                            className={`flex justify-between items-start px-4 py-2 text-xs ${
                              item.nivel === 1 
                                ? 'pl-8 text-muted-foreground bg-muted' 
                                : 'font-semibold text-foreground'
                            }`}
                          >
                            <span className="flex items-center gap-1.5">
                              {item.nivel === 1 && (
                                <span className="text-primary text-xs">▸</span>
                              )}
                              <span>{item.descricao}</span>
                            </span>
                            
                            <span className={`
                              ${safeNum(item.valor) > 0 ? 'font-semibold text-foreground' : 'text-muted-foreground'}
                              ml-4 whitespace-nowrap
                            `}>
                              {safeNum(item.valor) > 0 ? `R$ ${fmtBRL(item.valor)}` : '−'}
                            </span>
                          </div>
                        ))}
                        
                        {/* Total */}
                        <div className="flex justify-between items-center px-4 py-3 bg-primary/5 font-bold text-sm">
                          <span className="uppercase text-foreground">Total</span>
                          <span className="text-primary text-lg">
                            R$ {fmtBRL(linha.situacao_atual?.valor)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Fidelidade */}
                  <div className="px-4 py-3 border-t border-border bg-muted">
                    {linha.situacao_atual.data_fidelidade ? (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-primary" />
                        <p className="text-xs text-muted-foreground">Fidelidade até:</p>
                        <Badge variant="outline" className="text-xs font-semibold text-primary border-primary">
                          {new Date(linha.situacao_atual.data_fidelidade).toLocaleDateString('pt-BR')}
                        </Badge>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-success" />
                        <p className="text-xs text-success font-medium">Sem fidelidade</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Alert Proposta Consolidada */}
                <div className="p-3 bg-muted rounded-lg border border-border">
                  <div className="flex items-start gap-2">
                    <div className="text-primary text-xl">ℹ️</div>
                    <div>
                      <p className="text-xs font-semibold text-primary mb-0.5 uppercase tracking-wide">
                        Proposta Consolidada
                      </p>
                      <p className="text-xs text-muted-foreground">
                        A proposta de otimização é calculada para a conta como um todo. 
                        Veja a seção "Proposta de Otimização" acima para economia total.
                      </p>
                    </div>
                  </div>
                </div>

              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
